import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { WordImageUrlPair } from "../features/words/types";
import { getRandomWordsWithImages } from "../features/words/api/getRandomWordsWithImages";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { ContactSupport } from "@mui/icons-material";
import ContactDialog from "./ContactDialog";
import { logGameCheck } from "../features/analytics";

const GameComponent: React.FC = () => {
  const [words, setWords] = useState<WordImageUrlPair[]>([]);
  const [choices, setChoices] = useState<string[]>([]);
  // answers: array of word or null, index matches image
  const [answers, setAnswers] = useState<(string | null)[]>([null, null, null]);
  const [result, setResult] = useState<string | null>(null);
  const [isVerifiedCorrect, setIsVerifiedCorrect] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactWord, setContactWord] = useState<string | null>(null);

  const startNewGame = () => {
    setResult(null);
    setAnswers([null, null, null]);
    setIsVerifiedCorrect(false);
    getRandomWordsWithImages().then((ws) => {
      setWords(ws);
      setChoices(ws.map((w) => w.word).sort(() => Math.random() - 0.5));
    });
  };

  useEffect(() => {
    startNewGame();
    // eslint-disable-next-line
  }, []);

  // Drag and drop logic
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { destination, draggableId } = result;
    // If dropped in a drop zone (image)
    if (destination.droppableId.startsWith("image-")) {
      const imgIdx = parseInt(destination.droppableId.split("-")[1], 10);
      const newAnswers = [...answers];
      // If there is already a word in this drop zone, put it back to choices
      const replacedWord = newAnswers[imgIdx];
      // Remove word from any previous image drop zone
      const prevIdx = answers.findIndex((w) => w === draggableId);
      if (prevIdx !== -1) {
        newAnswers[prevIdx] = null;
      }
      newAnswers[imgIdx] = draggableId;
      setAnswers(newAnswers);
      setChoices((prev) => {
        let updated = prev.filter((w) => w !== draggableId);
        if (replacedWord && !updated.includes(replacedWord)) {
          updated = [...updated, replacedWord];
        }
        return updated;
      });
    } else if (destination.droppableId === "choices") {
      // Dragged back to choices
      // Find which image it was removed from
      const imgIdx = answers.findIndex((w) => w === draggableId);
      if (imgIdx !== -1) {
        const newAnswers = [...answers];
        newAnswers[imgIdx] = null;
        setAnswers(newAnswers);
        // Only add back to choices if not already present
        setChoices((prev) =>
          prev.includes(draggableId) ? prev : [...prev, draggableId],
        );
      }
    }
  };

  const checkAnswers = () => {
    const isCorrect = answers.every((ans, i) => ans === words[i].word);
    logGameCheck(isCorrect);
    if (isCorrect) {
      setResult("Bravo ! Toutes les réponses sont correctes.");
      setIsVerifiedCorrect(true);
    } else {
      setResult("Dommage, essayez encore !");
      setIsVerifiedCorrect(false);
    }
  };

  if (words.length < 3) {
    return <Typography>Chargement du jeu...</Typography>;
  }

  return (
    <Box sx={{ mx: 2, textAlign: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Typography variant="h5" color="secondary" gutterBottom sx={{ mr: 1 }}>
          Associez chaque mot à la bonne image
        </Typography>
        <IconButton
          size="small"
          aria-label="Nouvelle partie"
          onClick={startNewGame}
          sx={{ ml: 1 }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Grid container spacing={3} justifyContent="center">
          {words.map((word, idx) => (
            <Grid item xs={4} key={word.word}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ position: "relative" }}>
                  <IconButton
                    size="small"
                    aria-label="Signaler un contenu inapproprié"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      zIndex: 1,
                    }}
                    onClick={() => {
                      setContactWord(word.word);
                      setContactDialogOpen(true);
                    }}
                  >
                    <ContactSupport color="secondary" fontSize="small" />
                  </IconButton>
                  <ContactDialog
                    isOpen={contactDialogOpen}
                    onClose={() => setContactDialogOpen(false)}
                    word={contactWord || ""}
                  />
                  <img
                    src={word.image_url}
                    alt={word.word}
                    style={{ width: "100%", maxWidth: 180, height: "auto" }}
                  />
                </Box>
                <Droppable droppableId={`image-${idx}`} direction="vertical">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: 48,
                        mt: 2,
                        background: snapshot.isDraggingOver
                          ? "#f0f0f0"
                          : "#fafafa",
                        border: "1px dashed #bbb",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {answers[idx] ? (
                        <Draggable
                          draggableId={answers[idx]!}
                          index={idx}
                          key={answers[idx]!}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                m: 0.5,
                                px: 2,
                                py: 1,
                                backgroundColor: snapshot.isDragging
                                  ? "primary.main"
                                  : "primary.light",
                                color: snapshot.isDragging
                                  ? "white"
                                  : "primary.contrastText",
                                borderRadius: 2,
                                boxShadow: snapshot.isDragging ? 3 : 1,
                                cursor: "grab",
                                fontWeight: "bold",
                                fontSize: 16,
                                border: "1px solid",
                                borderColor: snapshot.isDragging
                                  ? "primary.dark"
                                  : "primary.main",
                                transition: "background 0.2s",
                              }}
                            >
                              {answers[idx]}
                            </Box>
                          )}
                        </Draggable>
                      ) : (
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: 14 }}
                        >
                          Glissez le mot ici
                        </Typography>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 4, pb: 2 }}>
          <Droppable droppableId="choices" direction="horizontal">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  minHeight: 56,
                }}
              >
                {choices.map((choice, i) => (
                  <Draggable draggableId={choice} index={i} key={choice}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          m: 0.5,
                          px: 2,
                          py: 1,
                          backgroundColor: "white",
                          color: "primary.main",
                          borderRadius: 2,
                          cursor: "grab",
                          fontWeight: "bold",
                          fontSize: 16,
                          border: "1px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        {choice}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Button
            variant="contained"
            color={isVerifiedCorrect ? "success" : "secondary"}
            onClick={isVerifiedCorrect ? startNewGame : checkAnswers}
            disabled={answers.some((a) => !a)}
          >
            {isVerifiedCorrect ? "Rejouer" : "Vérifier"}
          </Button>
          <IconButton
            size="medium"
            aria-label="Nouvelle partie"
            onClick={startNewGame}
            sx={{ ml: 3 }}
          >
            <RefreshIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Box sx={{ pb: 2, textAlign: "center" }}>
          {result && (
            <Typography
              sx={{ mt: 2, ml: 2 }}
              color={result.includes("Bravo") ? "success.main" : "error.main"}
            >
              {result}
            </Typography>
          )}
        </Box>
      </DragDropContext>
    </Box>
  );
};

export default GameComponent;
